import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, Spin, Switch, Tag, message } from 'antd';
import { FieldTimeOutlined, SaveOutlined } from '@ant-design/icons';
import { get, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './GenerationConfigPage.css';

interface GenerationScale {
  key: string;
  label: string;
  duration_seconds: number;
  target_words: number;
  max_words: number;
  enabled: boolean;
}

interface GenerationConfig {
  config_key: string;
  active_scale_key: string;
  scales: GenerationScale[];
  updated_at?: string;
}

const GenerationConfigPage: React.FC = () => {
  const [form] = Form.useForm<GenerationConfig>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<GenerationConfig | null>(null);
  const watchedScales = Form.useWatch('scales', form);
  const scales = useMemo(() => watchedScales || [], [watchedScales]);

  const activeOptions = useMemo(
    () =>
      scales
        .filter((item) => item?.enabled)
        .map((item) => ({
          label: `${item.label} · 约 ${Math.round((item.duration_seconds || 0) / 60)} 分钟`,
          value: item.key,
        })),
    [scales],
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<GenerationConfig>(API_ENDPOINTS.generation_config.config);
      setConfig(data);
      form.setFieldsValue(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载生成配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await put(API_ENDPOINTS.generation_config.config, {
        active_scale_key: values.active_scale_key,
        scales: values.scales,
      });
      message.success('生成管理配置已保存');
      await loadConfig();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="generation-config-page">
      <h1 className="page-title">生成管理</h1>
      <p className="generation-config-desc">
        控制用户端每次生成的文案规模和预估语音时长，保存后新生成内容立即按当前启用档位执行。
      </p>

      <Card bordered={false} className="generation-config-card">
        <Spin spinning={loading}>
          {config && (
            <Space wrap className="generation-config-summary">
              <Tag color="pink">当前配置: {config.config_key}</Tag>
              <Tag color="green">档位数量: {config.scales.length}</Tag>
              {config.updated_at && <Tag color="blue">最近更新: {new Date(config.updated_at).toLocaleString()}</Tag>}
            </Space>
          )}

          <Alert
            type="info"
            showIcon
            message="测试阶段建议使用「测试 1 分钟」。切换到 3/5/15 分钟后，会影响 AI 文案字数和 TTS 合成长度。"
            description="这里的“预估字数”会写入生成提示词，“最多字数”会作为服务端兜底截断，避免异常生成过长内容。"
            style={{ marginBottom: 16 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item
              label="当前启用档位"
              name="active_scale_key"
              rules={[{ required: true, message: '请选择当前启用档位' }]}
            >
              <Select
                options={activeOptions}
                placeholder="请选择生成档位"
                suffixIcon={<FieldTimeOutlined />}
              />
            </Form.Item>

            <Form.List name="scales">
              {(fields) => (
                <div className="generation-scale-list">
                  {fields.map((field) => {
                    const scale = scales[field.name];
                    return (
                      <Card
                        key={field.key}
                        size="small"
                        className="generation-scale-card"
                        title={
                          <Space>
                            <FieldTimeOutlined />
                            <span>{scale?.label || '生成档位'}</span>
                            {scale?.enabled ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>}
                          </Space>
                        }
                      >
                        <div className="generation-scale-grid">
                          <Form.Item
                            label="档位标识"
                            name={[field.name, 'key']}
                            rules={[{ required: true, message: '请输入档位标识' }]}
                          >
                            <Input disabled />
                          </Form.Item>

                          <Form.Item
                            label="档位名称"
                            name={[field.name, 'label']}
                            rules={[{ required: true, message: '请输入档位名称' }]}
                          >
                            <Input placeholder="例如 3 分钟" />
                          </Form.Item>

                          <Form.Item
                            label="生成时长（秒）"
                            name={[field.name, 'duration_seconds']}
                            rules={[{ required: true, message: '请输入生成时长' }]}
                          >
                            <InputNumber min={30} max={3600} step={30} addonAfter="秒" />
                          </Form.Item>

                          <Form.Item
                            label="预估字数"
                            name={[field.name, 'target_words']}
                            rules={[{ required: true, message: '请输入预估字数' }]}
                          >
                            <InputNumber min={50} max={10000} step={10} addonAfter="字" />
                          </Form.Item>

                          <Form.Item
                            label="最多字数"
                            name={[field.name, 'max_words']}
                            rules={[{ required: true, message: '请输入最多字数' }]}
                          >
                            <InputNumber min={50} max={12000} step={10} addonAfter="字" />
                          </Form.Item>

                          <Form.Item label="是否启用" name={[field.name, 'enabled']} valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                          </Form.Item>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Form.List>
          </Form>
        </Spin>
      </Card>

      <div className="generation-config-actions">
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存配置
        </Button>
      </div>
    </div>
  );
};

export default GenerationConfigPage;
